import { useState } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  updateProfile
} from "firebase/auth";
import { ArrowRight, Check, ShieldCheck } from "lucide-react";
import { auth } from "../firebase";
import { ALLOWED_EMAIL_DOMAINS, isAllowedEmail, normalizeEmail } from "../utils";

const errorMessages = {
  "auth/email-already-in-use": "Für diese E-Mail existiert bereits ein Konto.",
  "auth/invalid-credential": "E-Mail oder Passwort ist nicht korrekt.",
  "auth/invalid-email": "Bitte eine gültige E-Mail-Adresse eingeben.",
  "auth/weak-password": "Das Passwort muss mindestens sechs Zeichen enthalten.",
  "auth/too-many-requests": "Zu viele Versuche. Bitte später erneut probieren."
};

export default function AuthScreen() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function update(event) {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    const email = normalizeEmail(form.email);
    if (!isAllowedEmail(email)) {
      setError("Registrierung und Anmeldung sind nur mit einer freigegebenen MarkenGroup-E-Mail möglich.");
      return;
    }
    if (mode === "register" && form.name.trim().length < 2) {
      setError("Bitte den vollständigen Namen eingeben.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        const credential = await createUserWithEmailAndPassword(auth, email, form.password);
        await updateProfile(credential.user, { displayName: form.name.trim() });
        await sendEmailVerification(credential.user);
      } else {
        await signInWithEmailAndPassword(auth, email, form.password);
      }
    } catch (authError) {
      setError(errorMessages[authError.code] || authError.message || "Anmeldung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-layout">
      <section className="auth-story">
        <div className="wordmark"><span>MG</span><strong>MarkenGroup</strong></div>
        <div>
          <p className="eyebrow light">LeadMagnet Workspace</p>
          <h1>Aus Kontakten werden persönliche Gespräche.</h1>
          <p className="story-copy">Ein ruhiger, fokussierter Arbeitsbereich für Prospects, Gesprächsnotizen und individuelle Website-Previews.</p>
        </div>
        <ul className="feature-list">
          <li><Check /> Zentrale Kontaktübersicht</li>
          <li><Check /> Chronologische Gesprächsnotizen</li>
          <li><Check /> Persönliche Vorschau- und E-Mail-Links</li>
        </ul>
      </section>

      <section className="auth-form-wrap">
        <form className="auth-card" onSubmit={submit}>
          <div className="auth-icon"><ShieldCheck /></div>
          <p className="eyebrow">Interner Zugang</p>
          <h2>{mode === "login" ? "Willkommen zurück" : "Konto erstellen"}</h2>
          <p className="muted">Nur für verifizierte Adressen von {ALLOWED_EMAIL_DOMAINS.join(" und ")}.</p>
          {mode === "register" && (
            <label className="field">
              <span>Vollständiger Name</span>
              <input name="name" value={form.name} onChange={update} autoComplete="name" required placeholder="Andreas Koch" />
            </label>
          )}
          <label className="field">
            <span>E-Mail</span>
            <input type="email" name="email" value={form.email} onChange={update} autoComplete="email" required placeholder="name@marken-group.com" />
          </label>
          <label className="field">
            <span>Passwort</span>
            <input type="password" name="password" value={form.password} onChange={update} autoComplete={mode === "login" ? "current-password" : "new-password"} minLength="6" required />
          </label>
          {error && <div className="error-box" role="alert">{error}</div>}
          <button className="button primary wide" disabled={busy}>
            {busy ? "Bitte warten …" : mode === "login" ? "Anmelden" : "Registrieren"}<ArrowRight />
          </button>
          <button type="button" className="text-button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
            {mode === "login" ? "Noch kein Konto? Jetzt registrieren" : "Bereits registriert? Zur Anmeldung"}
          </button>
        </form>
      </section>
    </main>
  );
}
